import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Collapse,
  Paper,
  Popper,
  ClickAwayListener,
  Box,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../common/Icon';
import { getMenuConfig } from '../../config/menuConfig';
import { menuToPath, resolveMenuIdFromPath } from '../../config/routeConfig';

const DRAWER_WIDTH_OPEN = 240;
const DRAWER_WIDTH_CLOSED = 90;

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: open ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: open ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.sidebar,
    color: theme.palette.primary.contrastText,
  },
}));


const Sidebar = ({ open, isMobile, onClose }) => {
  const { hasAnyRole, getActiveCompany, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverItem, setPopoverItem] = useState(null);

  const activeCompany = getActiveCompany();
  const menuConfig = getMenuConfig(activeCompany?.id);

  const activePage = resolveMenuIdFromPath(location.pathname, activeCompany?.id);
  const filteredMenuItems = menuConfig.filter((item) => hasAnyRole(item.roles));

  const handleNavigate = (menuId) => {
    const path = menuToPath[menuId];
    if (path) {
      navigate(path);
      if (isMobile && onClose) {
        onClose();
      }
    }
  };

  const handleMenuClick = async (item) => {
    if (item.id === 'logout') {
      try {
        await logout();
      } finally {
        navigate('/login', { replace: true });
        if (isMobile && onClose) {
          onClose();
        }
      }
      return;
    }

    // Always navigate when clicking a menu item
    handleNavigate(item.id);

    // Also toggle submenu if item has children and sidebar is open
    if (item.children && open) {
      setExpandedMenus((prev) => ({
        ...prev,
        [item.id]: !prev[item.id],
      }));
    }
  };

  const handleMouseEnter = (event, item) => {
    if (!open && item.children) {
      setPopoverAnchor(event.currentTarget);
      setPopoverItem(item);
    }
  };

  const handleMouseLeave = () => {
    // Delay closing to allow mouse to move to popover
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setPopoverItem(null);
  };

  const handleChildClick = (childId) => {
    handleNavigate(childId);
    handlePopoverClose();
  };

  const isChildActive = (item) => {
    if (!item.children) return false;
    return item.children.some((child) => child.id === activePage);
  };

  const drawerContent = (
    <>
      <Toolbar />
      <List sx={{ px: 1 }}>
        {filteredMenuItems.map((item) => (
          <Box key={item.id}>
            <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
              <ListItemButton
                selected={activePage === item.id || isChildActive(item)}
                onClick={() => handleMenuClick(item)}
                onMouseEnter={(e) => handleMouseEnter(e, item)}
                onMouseLeave={handleMouseLeave}
                sx={{
                  minHeight: 48,
                  width: '100%',
                  flexDirection: open ? 'row' : 'column',
                  justifyContent: open ? 'flex-start' : 'center',
                  alignItems: 'center',
                  px: open ? 2 : 1,
                  py: open ? 1 : 1.5,
                  borderRadius: 1,
                  border: '1px solid transparent',
                  color: 'rgba(255,255,255,0.82)',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255,255,255,0.18)',
                    borderColor: 'rgba(255,255,255,0.40)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'rgba(255,255,255,0.22)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    borderColor: 'rgba(255,255,255,0.18)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: open ? 'auto' : '100%',
                    position: 'relative',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: 'center',
                      color: (activePage === item.id || isChildActive(item))
                        ? '#FFFFFF'
                        : 'rgba(255,255,255,0.7)',
                    }}
                  >
                    <Icon name={item.icon} />
                  </ListItemIcon>
                  {item.children && !open && (
                    <ChevronRightIcon
                      sx={{
                        fontSize: 16,
                        position: 'absolute',
                        right: 2,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'rgba(255,255,255,0.55)',
                      }}
                    />
                  )}
                </Box>

                {open ? (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: activePage === item.id || isChildActive(item) ? 700 : 500,
                      color: activePage === item.id || isChildActive(item) ? '#FFFFFF' : 'rgba(255,255,255,0.78)',
                    }}
                    sx={{ ml: 2 }}
                  />
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      width: '100%',
                      mt: 0.5,
                      fontSize: 10,
                      textAlign: 'center',
                      fontWeight: activePage === item.id || isChildActive(item) ? 700 : 500,
                      color: (activePage === item.id || isChildActive(item))
                        ? '#FFFFFF'
                        : 'rgba(255,255,255,0.7)',
                      lineHeight: 1.2,
                    }}
                  >
                    {item.label}
                  </Typography>
                )}

                {item.children && open && (
                  expandedMenus[item.id] ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>
            </ListItem>

            {item.children && open && (
              <Collapse in={expandedMenus[item.id]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children
                    .filter((child) => hasAnyRole(child.roles))
                    .map((child) => (
                      <ListItem key={child.id} disablePadding sx={{ display: 'block' }}>
                        <ListItemButton
                          selected={activePage === child.id}
                          onClick={() => handleNavigate(child.id)}
                          sx={{
                            minHeight: 40,
                            pl: 4,
                            pr: 2,
                            borderRadius: 1,
                            transition: 'all 0.12s ease',
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(255,255,255,0.18)',
                              boxShadow: 'inset 3px 0 0 0 rgba(255,255,255,0.9)',
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.06)',
                              boxShadow: 'inset 3px 0 0 0 rgba(255,255,255,0.4)',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: 2,
                              justifyContent: 'center',
                              color: activePage === child.id ? '#FFFFFF' : 'rgba(255,255,255,0.7)',
                            }}
                          >
                            <Icon name={child.icon} />
                          </ListItemIcon>
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              fontWeight: activePage === child.id ? 700 : 500,
                              color: activePage === child.id ? '#FFFFFF' : 'rgba(255,255,255,0.78)',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>

      <Popper
        open={Boolean(popoverAnchor) && Boolean(popoverItem)}
        anchorEl={popoverAnchor}
        placement="right-start"
        sx={{ zIndex: 1300 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handlePopoverClose}>
          <Paper
            elevation={8}
            sx={{
              minWidth: 200,
              py: 1,
              borderRadius: 2,
            }}
            onMouseLeave={handlePopoverClose}
          >
            {popoverItem?.children
              ?.filter((child) => hasAnyRole(child.roles))
              .map((child) => (
                <ListItemButton
                  key={child.id}
                  selected={activePage === child.id}
                  onClick={() => handleChildClick(child.id)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                    <Icon name={child.icon} />
                  </ListItemIcon>
                  <ListItemText primary={child.label} />
                </ListItemButton>
              ))}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH_OPEN,
            boxSizing: 'border-box',
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: 'background.sidebar',
            color: 'primary.contrastText',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <StyledDrawer variant="permanent" open={open}>
      {drawerContent}
    </StyledDrawer>
  );
};

export default Sidebar;
